import { BEIGE_600, BLACK_TEXT, GRAY_400, GRAY_700, GRAY_800 } from 'constants/styling/theme'

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import { AssignmentAttachedFileResponse } from 'dataQueries'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { CircleIcon } from 'components/common/CircleIcon'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MUITooltip } from '../MUITooltip'
import { MessageAttachedFile } from '../Threads'
import React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import moment from 'moment-timezone'
import { saveAs } from 'file-saver'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props AttachedFiles properties
 */
export interface Props {
  /** Attached files list */
  attachedFiles: MessageAttachedFile[] | AssignmentAttachedFileResponse[]
  /** Custom action on download */
  onDownload?: () => void
}

/**
 * @component AttachedFiles
 * This component renders a list of attached files with options to download them.
 * TODO: this component should also render "Received" date, when implemented in the backend endpoint
 * 
 * @example
 * <AttachedFiles 
 *   attachedFiles={attachedFiles}
 * />
 */
export const AttachedFiles: React.FC<Props> = ({ attachedFiles, onDownload }) => {
  const { t } = useTranslation(['gallery', 'common'])
  return (
    <Stack minWidth="35rem" maxWidth="40rem" marginTop=".2rem">

      <Stack direction="row" alignItems="center" gap=".4rem">
        <Typography
          variant="text-sm"
          color={GRAY_700}
          fontWeight="medium"
          paddingBottom=".6rem"
        >
          {t('order_informations.expense_documents')}
        </Typography>

        <MUITooltip content={t('order_informations.expense_documents_tooltip')}>
          <InfoOutlinedIcon fontSize="medium" sx={{ color: GRAY_800 }} />
        </MUITooltip>
      </Stack>

      {attachedFiles.map((attachedFile) =>
        <BorderBoxWrapper
          padding={0.5}
          marginTop={1}
          display="flex"
          flexDirection="column"
          borderColor={GRAY_400}
          key={attachedFile.id}
        >
          <Grid container flexDirection="row" alignItems="center">

            <Grid item xs={1}>
              <CircleIcon
                size="2.4rem"
                circleColor={BEIGE_600}
                icon={<ArticleOutlinedIcon fontSize="medium" sx={{ color: GRAY_800 }} />}
              />
            </Grid>

            <Grid item xs={11} display="flex" alignItems="center" justifyContent="space-between">

              <Typography
                variant="text-xs"
                color={BLACK_TEXT}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  cursor: 'help',
                }}
                title={attachedFile.fileName}
              >
                {attachedFile.fileName}
              </Typography>

              <IconButton onClick={() => {
                onDownload?.()
                saveAs(attachedFile.fileUrl, attachedFile.fileName)
              }}>
                <CloudDownloadOutlinedIcon fontSize="medium" sx={{ color: GRAY_800 }} />
              </IconButton>

            </Grid>

          </Grid>

          {'receivedDate' in attachedFile &&
            <Grid container flexDirection="row" alignItems="center" spacing={0.5}>

              <Grid item xs={1} visibility="hidden"></Grid>

              <Grid item xs={11}>
                <Typography
                  variant="text-xs"
                  color={GRAY_700}
                  fontWeight="medium"
                >
                  {t('received')} {moment(attachedFile.receivedDate).format('DD/MM/YYYY')}
                </Typography>
              </Grid>

            </Grid>
          }

        </BorderBoxWrapper>
      )}
    </Stack>
  )
}
